import { filterClasses } from "@swivl-mono/helpers";
import { Channel, IsSessionTakenOver, LocationVarKey, SessionInterface, UserInterface } from "@swivl-mono/types";
import { Button } from "@swivl-mono/ui";
import { useEffect, useMemo, useRef, useState } from "react";

import { ChannelColors } from "../../../../Helpers/Conversations/Conversations.Helper";
import useHasPendingUpdates from "../../../../Hooks/useHasPendingUpdates";
import { useLocationVariables } from "../../../../Hooks/useLocationVariables";
import { SessionMessageData } from "../../../../Hooks/useSessionMessageData";
import { useUrlState } from "../../../../Hooks/useUrlState";
import { Member } from "../../../../Models/Member/Member.Current";
import { PendingUpdateType } from "../../../../Models/Pending/Pending.Model";
import { UsersModel } from "../../../../Models/Users/Users.Model";

export const validChannels = { web: "Web", sms: "SMS", voice: "Voice" } as const;

interface BaseMultiChannelColumnHeaderProps {
  session?: SessionInterface;
  selectedUser?: UserInterface | undefined;
  channel: Channel;
  sessionMessageData?: { [userId: number]: { [sessionId: number]: SessionMessageData } };
}

export function MultiChannelColumnHeader({ session, channel, selectedUser, sessionMessageData }: BaseMultiChannelColumnHeaderProps) {
  const orgVars = useLocationVariables([LocationVarKey.swivlHasVoice]);
  const swivlHasVoice = Boolean(Number(orgVars?.swivlHasVoice));
  const tabContainerRef = useRef<HTMLDivElement | null>(null);
  const [channelIndicatorXPosition, setChannelIndicatorXPosition] = useState<number | null>(null);
  // const [newMessageIndicatorXPositions, setNewMessageIndicatorXPositions] = useState<{ [channel: string]: number | null }>({});
  const { setSearchParam } = useUrlState();
  const isPending = useHasPendingUpdates([
    { type: PendingUpdateType.USER_ARCHIVED_STATE, id: selectedUser?.id || "" },
    { type: PendingUpdateType.SESSION_ESCALATION, id: session?.id || "" }
  ]);

  // todo: new message indicator - add back in after talks with design
  // set new message indicator position
  // useEffect(() => {
  //   const updateNewMessageInidicatorPositions = () => {

  //     if (!tabContainerRef.current || !selectedUser) return;
  //     const userSessions = sessionMessageData?.[selectedUser.id] || {};
  //     const newXPositions: { [channel: string]: number | null } = {};

  //     Object.values(userSessions).forEach((messageData) => {
  //       if (messageData?.newMessageCount > 0) {
  //         const newMessageTab = tabContainerRef.current?.querySelector(`.channel-button[data-channel="${messageData.channel}"]`) as HTMLElement | null;

  //         if (newMessageTab) {
  //           const relativeLeft = newMessageTab.offsetLeft + newMessageTab.offsetWidth + 8;
  //           newXPositions[messageData.channel] = relativeLeft;
  //         }
  //       }
  //     });

  //     setNewMessageIndicatorXPositions(newXPositions);
  //   }

  //   updateNewMessageInidicatorPositions();
  //   window.addEventListener("resize", updateNewMessageInidicatorPositions);

  //   return () => {
  //     window.removeEventListener("resize", updateNewMessageInidicatorPositions);
  //   };
  // }, [sessionMessageData, selectedUser])

  // handle active channel tab indicator
  useEffect(() => {
    const updateIndicatorPosition = () => {
      if (tabContainerRef.current) {
        const tabContainerRect = tabContainerRef?.current?.getBoundingClientRect();
        const activeTab = tabContainerRef.current.querySelector(`.channel-button[data-channel="${channel}"]`);
        if (activeTab) {
          const { left: tabLeft, width } = activeTab.getBoundingClientRect();
          const relativeLeft = tabLeft - tabContainerRect.left;
          setChannelIndicatorXPosition(relativeLeft + width / 2 - 29);
        }
      }
    };

    updateIndicatorPosition();
    window.addEventListener("resize", updateIndicatorPosition);

    return () => {
      window.removeEventListener("resize", updateIndicatorPosition);
    };
  }, [channel]);

  const handleSetChannel = (channel: string) => {
    setSearchParam("channel", channel);
  };

  const isChannelDisabled = (channel: Channel) => {
    if (!selectedUser) return true;

    const hasChannels = selectedUser.channels && selectedUser.channels.length > 0;

    // handle non-voice channels
    if (channel !== Channel.SMS && channel !== Channel.VOICE) {
      return !(hasChannels && selectedUser.channels.includes(channel));
    }

    // sms and voice require phone number, but might also be in channels array
    if (channel === Channel.SMS || channel === Channel.VOICE) {
      return !(selectedUser.phoneNumber || (hasChannels && selectedUser.channels.includes(channel)));
    }

    return true;
  };

  const channelValues = {
    [Channel.WEB]: ChannelColors[Channel.WEB].bg,
    [Channel.SMS]: ChannelColors[Channel.SMS].bg,
    [Channel.VOICE]: ChannelColors[Channel.VOICE].bg
  };

  if (!selectedUser) {
    console.warn("No selected user for MultiChannelColumnHeader");
    return null;
  }

  const actionButton = useMemo(() => {
    if (session && IsSessionTakenOver(session, Member.id)) {
      return (
        <Button
          loading={isPending}
          onClick={() => {
            const sessionEscalationId = session.escalationId || session.escalation?.id;
            if (sessionEscalationId) {
              UsersModel.endTakeoverOfSession(session.id, sessionEscalationId);
            }
          }}
        >
          End Takeover
        </Button>
      );
    }

    if (selectedUser.isArchived) {
      return (
        <Button loading={isPending} onClick={() => UsersModel.setArchivedForUser(selectedUser.id, false)}>
          Open
        </Button>
      );
    }

    return (
      <Button loading={isPending} onClick={() => UsersModel.setArchivedForUser(selectedUser.id, true)}>
        Close
      </Button>
    );
  }, [isPending, selectedUser, session]);

  return (
    <div className="relative flex h-12 items-center border-b border-grey-100 bg-white pr-4 pl-8">
      {/* channel select tabs */}
      <div className="flex h-full grow items-center gap-8" ref={tabContainerRef}>
        {Object.entries(validChannels).map(([key, value]) => (
          <button
            className={filterClasses(
              "channel-button cursor-pointer text-sm font-semibold transition-colors duration-100",
              "disabled:cursor-not-allowed disabled:text-grey-600",
              ChannelColors[value].hover
            )}
            data-channel={value}
            disabled={isChannelDisabled(value as Channel)}
            key={key}
            onClick={() => handleSetChannel(value)}
          >
            {value}
          </button>
        ))}

        {/* todo: new message indicator - add back in after talks with design */}
        {/* new message indicator */}
        {/* {Object.entries(newMessageIndicatorXPositions).map(([channel, position]) =>
          position !== null ? (
            <div
              className="absolute size-1.5 rounded-full bg-red-500 top-3"
              key={channel}
              style={{ left: `${position}px` }}
            />
          ) : null
        )} */}

        {/* selected channel indicator / border */}
        {channelIndicatorXPosition !== null && (
          <div
            className={filterClasses("absolute bottom-0 h-[3px] w-[58px] transition-all duration-300 ease-in-out", channelValues[channel])}
            style={{
              transform: `translateX(${channelIndicatorXPosition}px)`
            }}
          />
        )}
      </div>
      {/* archive / end takeover button */}
      {actionButton}
    </div>
  );
}
